import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Save, ClipboardList, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AttendanceMaskSelector } from '@/components/ehr/AttendanceMaskSelector';
import type { AttendanceTemplateContent } from '@/services/attendanceTemplates';
import {
  getMedicalRecordByAppointment,
  saveMedicalRecord,
} from '@/services/medicalRecords';

interface MedicalRecordEditorProps {
  appointmentId: string;
  patientId: string;
  /** Expõe o conteúdo da evolução para o modal de encerramento */
  onContentChange?: (content: string) => void;
}

function mapSaveErrorMessage(error?: string): string {
  const message = (error || '').toLowerCase();

  if (message.includes('405') || message.includes('method not allowed')) {
    return 'Falha de integração ao salvar o prontuário (método/rota inválidos). Tente novamente.';
  }
  if (message.includes('sessão') || message.includes('não autorizado') || message.includes('401')) {
    return 'Sessão expirada. Faça login novamente para salvar o prontuário.';
  }
  if (message.includes('permissão') || message.includes('403') || message.includes('acesso negado')) {
    return 'Você não tem permissão para editar este prontuário.';
  }
  if (
    message.includes('valid') ||
    message.includes('422') ||
    message.includes('400') ||
    message.includes('must be') ||
    message.includes('uuid')
  ) {
    return error || 'Dados inválidos para salvar o prontuário.';
  }

  return error || 'Erro ao salvar o prontuário.';
}

export const MedicalRecordEditor: React.FC<MedicalRecordEditorProps> = ({
  appointmentId,
  patientId: _patientId,
  onContentChange,
}) => {
  const [content, setContent] = useState('');
  const [summary, setSummary] = useState('');
  const [status, setStatus] = useState<'DRAFT' | 'FINALIZED'>('DRAFT');
  const [isSaving, setIsSaving] = useState(false);
  const savingLockRef = useRef(false);

  // Carregar dados existentes
  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const result = await getMedicalRecordByAppointment(appointmentId);
        if (result.success && result.data) {
          setContent(result.data.content || '');
          setSummary(result.data.clinicalSummary || '');
          setStatus(result.data.status || 'DRAFT');
        }
      } catch (err) {
        console.error('Falha ao carregar prontuário', err);
      }
    };
    if (appointmentId) fetchRecord();
  }, [appointmentId]);

  useEffect(() => {
    onContentChange?.(content);
  }, [content, onContentChange]);

  const handleSave = async (finalized = false) => {
    if (savingLockRef.current || isSaving || status === 'FINALIZED') {
      return;
    }

    savingLockRef.current = true;
    setIsSaving(true);

    try {
      const result = await saveMedicalRecord({
        appointmentId,
        content,
        clinicalSummary: summary,
        status: finalized ? 'FINALIZED' : 'DRAFT',
      });

      if (result.success) {
        toast.success(finalized ? 'Prontuário finalizado com sucesso!' : 'Rascunho salvo!');
        if (finalized) setStatus('FINALIZED');
        if (result.data?.content != null) {
          setContent(result.data.content);
        }
        if (result.data?.clinicalSummary != null) {
          setSummary(result.data.clinicalSummary);
        }
        return;
      }

      // Mantém o conteúdo digitado em caso de erro
      toast.error(mapSaveErrorMessage(result.error || result.message));
    } catch (err) {
      const message = err instanceof Error ? err.message : undefined;
      toast.error(mapSaveErrorMessage(message) || 'Falha na comunicação com o servidor');
    } finally {
      savingLockRef.current = false;
      setIsSaving(false);
    }
  };

  const formatMaskToHtml = (mask: AttendanceTemplateContent) => {
    let html = '';
    if (mask.chiefComplaint) html += `<h4>Queixas</h4><p>${mask.chiefComplaint}</p><br/>`;
    if (mask.hda) html += `<h4>História da Doença Atual (HDA)</h4><p>${mask.hda}</p><br/>`;
    if (mask.physicalExam) html += `<h4>Exame Físico</h4><p>${mask.physicalExam}</p><br/>`;
    if (mask.hypothesis) html += `<h4>Hipótese Diagnóstica</h4><p>${mask.hypothesis}</p><br/>`;
    if (mask.conduct) html += `<h4>Conduta</h4><p>${mask.conduct}</p><br/>`;
    if (mask.observations) html += `<h4>Observações</h4><p>${mask.observations}</p><br/>`;
    return html;
  };

  const handleApplyMask = (maskContent: AttendanceTemplateContent) => {
    const formattedHtml = formatMaskToHtml(maskContent);
    setContent(formattedHtml);
  };

  return (
    <Card className="h-full flex flex-col border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden">
      <CardHeader className="bg-neutral-50 dark:bg-neutral-900 border-b py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-teal-600" />
            Prontuário Eletrônico (PEP)
          </CardTitle>
          <div className="flex items-center gap-2">
            {status === 'DRAFT' && (
              <AttendanceMaskSelector
                onApply={handleApplyMask}
                disabled={false}
                currentContent={content.replace(/<[^>]*>?/gm, '').trim() ? { chiefComplaint: 'filled' } : undefined}
              />
            )}
            {status === 'FINALIZED' && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1 font-medium">
                <CheckCircle className="w-3 h-3" /> Finalizado
              </span>
            )}
          </div>
        </div>
      </CardHeader>

      <div className="flex-1 p-0 m-0">
        <div className="h-full flex flex-col">
          <div className="flex-1 overflow-auto">
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              placeholder="Descreva a evolução do paciente, queixas, exame físico e conduta..."
              className="h-full min-h-[300px]"
              readOnly={status === 'FINALIZED'}
            />
          </div>
          <div className="p-4 border-t bg-neutral-50 dark:bg-neutral-900 flex justify-between items-center">
            <div className="text-xs text-muted-foreground italic">
              {status === 'DRAFT' ? 'Rascunho habilitado' : 'Documento selado e imutável.'}
            </div>
            <div className="flex gap-2">
              {status === 'DRAFT' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSave(false)}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salvar Rascunho
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
